import React, { useMemo, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import AppChart, { ChartSeries } from 'shared/components/Chart/AppChart';
import ReportDataTable, {
  ReportColumn,
} from 'shared/components/Table/ReportDataTable';
import { infraTypes, getInfraDistricts } from 'shared/json/infrastructureData';
import DrillBreadcrumb from 'shared/components/Dashboard/DrillBreadcrumb';
import {
  Filters,
  EMPTY_FILTERS,
  drillHint,
  drillLevelLabel,
  drillSearchPlaceholder,
  scopeLabel,
} from 'shared/json/drilldown';
import {
  useGetSchoolInfraTypeDistrictsQuery,
  SchoolInfraTypeDistrictRow,
} from './Dashboard.service';
import './Infrastructure.scss';
import './InfrastructureComparison.scss';

interface InfrastructureComparisonProps {
  /** Currently selected infrastructure type key (from the sidebar). */
  activeType: string;
  /** Selected source (EMIS / UDISE+) — shown next to the title. */
  source?: 'emis' | 'udise';
  /** Numeric infrastructure type id for the API. */
  type: number;
  /** Frozen academic year id. */
  acdYr?: number | null;
  /** Classification filters from the dashboard header. */
  filters?: Filters;
  /** Header geography — drives and syncs the drill level. */
  districtId?: number | null;
  blockId?: number | null;
  district?: string | null;
  block?: string | null;
  /** Header-selected school — narrows the ranked bar + table to that school. */
  school?: string | null;
  /** Header-selected school id → scopes the query to that one school. */
  schoolId?: number | null;
  /** Header setters so bar clicks / Back update the top dropdowns. */
  onDistrictChange?: (value: string) => void;
  onBlockChange?: (value: string) => void;
  onSchoolChange?: (value: string) => void;
}

/**
 * When a school is selected in the header, narrow an entity array (the schools
 * of the block) to just that one school. Matches on the bare name (strips any
 * trailing " (udise)"). Falls back to the full list on no match.
 */
const narrowToSchool = <T,>(
  rows: T[],
  name: (r: T) => unknown,
  school?: string | null
): T[] => {
  if (!school) return rows;
  const want = school.trim().toLowerCase();
  const strip = (s: string) =>
    s
      .replace(/\s*\(\d[\d/]*\)\s*$/, '')
      .trim()
      .toLowerCase();
  const filtered = rows.filter((r) => strip(String(name(r) ?? '')) === want);
  return filtered.length > 0 ? filtered : rows;
};

// Metrics the children can be ranked/compared by. `lowerIsBetter` flips the
// color logic (a low Shortfall Gap is good, so below-state bars turn green).
const METRICS = [
  { label: 'Data Entry Compliance', value: 'entry', lowerIsBetter: false },
  {
    label: 'Availability vs Norms',
    value: 'availability',
    lowerIsBetter: false,
  },
  {
    label: 'School Coverage',
    value: 'coverage',
    lowerIsBetter: false,
  },
  { label: 'Good Condition', value: 'good', lowerIsBetter: false },
  // { label: 'Functional', value: 'functional', lowerIsBetter: false },
  { label: 'Shortfall Gap', value: 'gap', lowerIsBetter: true },
] as const;

type MetricKey = (typeof METRICS)[number]['value'];

type MetricRow = { id: number; name: string } & Record<MetricKey, number>;

/** Parse a "95.6%" string to the number 95.6. */
const pct = (v: string) => Number(String(v).replace('%', '')) || 0;

const n = (v: unknown) => Number(v) || 0;

// Per-metric value for one child. Prefers the API precomputed `*_pct`, else
// derives it from raw fields using the agreed formulas.
const availabilityPct = (r: SchoolInfraTypeDistrictRow) =>
  r.availability_pct != null
    ? n(r.availability_pct)
    : n(r.norms ?? r.required)
      ? n(r.available) / n(r.norms ?? r.required)
      : 0;
const coveragePct = (r: SchoolInfraTypeDistrictRow) =>
  r.coverage_pct != null
    ? n(r.coverage_pct)
    : n(r.schools)
      ? (n(r.schools_with ?? r.schoolsWith) / n(r.schools)) * 100
      : 0;
const functionalPct = (r: SchoolInfraTypeDistrictRow) =>
  r.functional_pct != null
    ? n(r.functional_pct)
    : n(r.available)
      ? (n(r.functional) / n(r.available)) * 100
      : 0;
const goodPct = (r: SchoolInfraTypeDistrictRow) => {
  if (r.good_pct != null) return n(r.good_pct);
  const denom =
    n(r.good) +
    n(r.need_repair ?? r.needRepair) +
    n(r.to_be_demolished ?? r.demolish);
  return denom ? (n(r.good) / denom) * 100 : 0;
};
const entryPct = (r: SchoolInfraTypeDistrictRow) =>
  r.entry_pct != null
    ? n(r.entry_pct)
    : n(r.expected ?? r.schools_expected)
      ? (n(r.submitted ?? r.schools_submitted) /
          n(r.expected ?? r.schools_expected)) *
        100
      : 0;
// gapPct = required/norms x100 (lower is better).
const gapPct = (r: SchoolInfraTypeDistrictRow) =>
  r.shortfall_pct != null
    ? n(r.shortfall_pct)
    : n(r.norms)
      ? (n(r.required ?? r.norms) / n(r.norms)) * 100
      : 0;

/**
 * Infrastructure Comparison view (Infrastructure nav tab -> Comparison sub-tab).
 *
 * Ranked horizontal bar chart of the current level (districts -> blocks ->
 * schools) for the selected metric, colored green/red vs the level average,
 * plus a full comparison table. Clicking a bar drills down and refetches
 * schoolinfratypedistricts for the next level. Data = API with mock fallback.
 */
const InfrastructureComparison: React.FC<InfrastructureComparisonProps> = ({
  activeType,
  source = 'emis',
  type,
  acdYr = null,
  filters = EMPTY_FILTERS,
  districtId = null,
  blockId = null,
  district = null,
  block = null,
  school = null,
  schoolId = null,
  onDistrictChange,
  onBlockChange,
  onSchoolChange,
}) => {
  const [metric, setMetric] = useState<MetricKey>('availability');

  const activeMeta =
    infraTypes.find((t) => t.key === activeType) ?? infraTypes[0];

  // Refetches whenever the type / drill level / filters change. When a school
  // is selected, wait for Infrastructure to resolve the correct infra ID first
  // so the chart never flashes block-level data.
  const waitingForSchool = !!school && schoolId == null;
  const { data, isFetching } = useGetSchoolInfraTypeDistrictsQuery(
    {
      type,
      ...(blockId != null
        ? { block_id: blockId }
        : districtId != null
          ? { district_id: districtId }
          : {}),
      ...(schoolId != null && { school_id: schoolId }),
      ...(filters.management[0] ? { manage_id: filters.management[0] } : {}),
      ...(filters.schoolType[0]
        ? { school_type_id: filters.schoolType[0] }
        : {}),
      ...(filters.category[0] ? { cate_id: filters.category[0] } : {}),
    },
    { skip: acdYr == null || waitingForSchool }
  );
  const loading = isFetching || waitingForSchool;

  const result = data?.result;
  const items = useMemo(() => result?.items ?? [], [result]);
  const groupKey = result?.group_key ?? 'district_id';
  const level = (result?.level ??
    (blockId != null
      ? 'school'
      : districtId != null
        ? 'block'
        : 'district')) as 'district' | 'block' | 'school';
  const levelNoun =
    level === 'school' ? 'Schools' : level === 'block' ? 'Blocks' : 'Districts';
  const comparisonScopeName = scopeLabel({ district, block, school });
  const canDrill = level !== 'school';

  const activeMetric = METRICS.find((m) => m.value === metric)!;
  const metricLabel = activeMetric.label;
  const lowerIsBetter = activeMetric.lowerIsBetter;

  // Per-child values for all six metrics (+ id for drill). From the API when
  // available, else the mock districts (four metrics the mock carries).
  const metricRows = useMemo<MetricRow[]>(() => {
    // The state-wide mock (getInfraDistricts) is a placeholder for the true
    // state/district-level view ONLY — it has no concept of scope, so it must
    // never stand in for a BLOCK or SCHOOL level (district_id/block_id was
    // sent and the API genuinely has no rows to return yet, or the request is
    // still settling). Falling back to it there silently re-shows the full
    // all-districts list even though the user is scoped to one
    // district/block (e.g. a locked Collector/Block login), which looks
    // exactly like the scope was never applied. Empty is the honest result.
    const base: MetricRow[] = items.length
      ? items.map((r) => ({
          id: Number(
            (r as Record<string, unknown>)[groupKey] ??
              r.district_id ??
              r.block_id ??
              r.school_id ??
              0
          ),
          name: String(r.name ?? r.district_name ?? ''),
          entry: entryPct(r),
          availability: availabilityPct(r),
          coverage: coveragePct(r),
          good: goodPct(r),
          functional: functionalPct(r),
          gap: gapPct(r),
        }))
      : school || level !== 'district'
        ? []
        : getInfraDistricts(activeType).map((r, i) => ({
            id: i + 1,
            name: r.name,
            entry: 0,
            availability: pct(r.availability),
            coverage: pct(r.coverage),
            good: pct(r.good),
            functional: 0,
            gap: pct(r.gap),
          }));
    // A selected school → just that one school's row (in sync with the header).
    return level === 'school'
      ? narrowToSchool(base, (r) => r.name, school)
      : base;
  }, [items, groupKey, activeType, level, school]);
  //console.log('metricRows', metricRows);
  // Ranked bar data (sorted by the chosen metric), colored vs the level average.
  const { labels, series, avg, sortedRows } = useMemo(() => {
    const sorted = [...metricRows].sort((a, b) =>
      lowerIsBetter ? a[metric] - b[metric] : b[metric] - a[metric]
    );
    const values = sorted.map((r) => r[metric]);
    const average = values.reduce((s, v) => s + v, 0) / (values.length || 1);
    const colors = values.map((v) => {
      const atOrAbove = lowerIsBetter ? v <= average : v >= average;
      return atOrAbove ? '#2e8b48' : '#d9584f';
    });
    return {
      sortedRows: sorted,
      labels: sorted.map((r) => r.name),
      avg: average,
      series: [
        { label: metricLabel, data: values, color: '#2e8b48', colors },
      ] as ChartSeries[],
    };
  }, [metricRows, metric, lowerIsBetter, metricLabel]);

  // Per-bar hover tooltip — shows ONLY the metric currently being viewed.
  const tooltipDetails = useMemo(() => {
    const map: Record<string, { label: string; value: string }[]> = {};
    sortedRows.forEach((r) => {
      map[r.name] = [{ label: metricLabel, value: `${r[metric].toFixed(1)}%` }];
    });
    return map;
  }, [sortedRows, metric, metricLabel]);

  const handleBarClick = (index: number) => {
    const row = sortedRows[index];
    console.log(index);
    if (!row) return;
    console.log(level);
    // Sync the top dropdowns; the resolved id flows back as districtId/blockId.
    if (level === 'district') onDistrictChange?.(row.name);
    else if (level === 'block') onBlockChange?.(row.name);
    else if (level === 'school') onSchoolChange?.(row.name);
  };

  const goBack = () => {
    if (blockId != null) onBlockChange?.('');
    else if (districtId != null) onDistrictChange?.('');
  };

  // Handle table row click for drill-down (same pattern as Infrastructure.tsx)
  const handleDistrictTableRowClick = (row: Record<string, unknown>) => {
    const name = String(row.name ?? '');
    if (!name) return;
    if (level === 'district') onDistrictChange?.(name);
    else if (level === 'block') onBlockChange?.(name);
    else if (level === 'school') onSchoolChange?.(name);
  };

  // Full comparison table rows. Same mock-fallback rule as metricRows above:
  // the unscoped state-wide mock only ever stands in for the true district
  // level, never for a block/school scope.
  const tableRows = useMemo(() => {
    const base = !items.length
      ? school || level !== 'district'
        ? []
        : (getInfraDistricts(activeType) as unknown as Record<
            string,
            unknown
          >[])
      : items.map((r) => ({
          name: String(r.name ?? r.district_name ?? ''),
          available: n(r.available),
          norms: n(r.norms ?? r.required),
          shortfall: n(r.shortfall),
          availability: `${availabilityPct(r).toFixed(1)}%`,
          coverage: `${coveragePct(r).toFixed(1)}%`,
          good: `${goodPct(r).toFixed(1)}%`,
        }));
    // A selected school → just that one school's row (in sync with the header).
    return level === 'school'
      ? narrowToSchool(base, (r) => r.name, school)
      : base;
  }, [items, activeType, level, school]);

  const tableColumns: ReportColumn[] = [
    {
      key: 'name',
      label: drillLevelLabel(level),
      type: 'link',
    },
    { key: 'available', label: 'Available', type: 'number' },
    { key: 'norms', label: 'Norms', type: 'number' },
    { key: 'shortfall', label: 'Shortfall', type: 'number' },
    { key: 'availability', label: 'Availability', type: 'text' },
    { key: 'coverage', label: 'Coverage', type: 'text' },
    { key: 'good', label: 'Good', type: 'text' },
  ];

  return (
    <div className="infra-cmp">
      {/* -- Header -- */}
      <div className="infra-cmp__head">
        <i className="pi pi-sort-alt infra-cmp__icon" />
        <h5 className="infra-cmp__title">
          {activeMeta.label} Comparison - {comparisonScopeName}
          <span className="infra-cmp__subtitle">
            {' '}
            {source === 'emis' ? 'EMIS' : 'UDISE+'}
          </span>
        </h5>
      </div>

      {/* -- Breadcrumb / back (when drilled in) -- */}
      <DrillBreadcrumb
        district={district}
        block={block}
        onBack={goBack}
        onDistrictChange={onDistrictChange}
        onBlockChange={onBlockChange}
        onSchoolChange={onSchoolChange}
      />

      {/* -- Metric selector row -- */}
      <div className="infra-cmp__toolbar">
        <div className="infra-cmp__metric-wrap">
          <span className="infra-cmp__metric-label">METRIC</span>
          <Dropdown
            value={metric}
            options={METRICS.map((m) => ({ label: m.label, value: m.value }))}
            onChange={(e) => setMetric(e.value)}
            className="infra-cmp__metric"
          />
        </div>
        <p className="infra-cmp__hint">
          <i className="pi pi-info-circle" /> Shortfall Gap is the one metric
          where a <strong>lower value is better</strong>.
        </p>
      </div>

      {/* -- Ranked bar chart -- */}
      <div className="infra-cmp__chart">
        <AppChart
          type="horizontalBar"
          title={`${metricLabel} ranked${
            canDrill
              ? ' - click a bar to drill down'
              : ' - click a bar to select'
          }`}
          icon="pi pi-chart-bar"
          iconColor="#2f5fb0"
          labels={labels}
          series={series}
          loading={loading}
          yTickSuffix="%"
          averageLine={avg}
          tooltipDetails={tooltipDetails}
          onBarClick={handleBarClick}
          height={620}
          barValueFormat={(value) => `${value.toFixed(1)}%`}
          headerRight={
            <div className="infra-cmp__legend">
              <span className="infra-cmp__legend-item">
                <span className="dot dot--bad" /> below avg
              </span>
              <span className="infra-cmp__legend-item">
                <span className="dot dot--good" /> at or above avg
              </span>
              <span className="infra-cmp__count-pill">
                {metricRows.length} {levelNoun}
              </span>
            </div>
          }
        />
        <p className="infra-cmp__avg-note">
          Dashed reference = {levelNoun.toLowerCase()} average ({metricLabel}):{' '}
          <strong>{avg.toFixed(1)}%</strong> ·{' '}
          {lowerIsBetter
            ? 'green = at or below average, red = above'
            : 'green = at or above average, red = below'}
        </p>
      </div>

      {/* -- Full comparison table -- */}
      <div className="infra-cmp__table">
        {/* Repeated next to the grid that drills — the breadcrumb at the top
            of this view sits above the ranked bar chart. */}
        <DrillBreadcrumb
          district={district}
          block={block}
          onBack={goBack}
          onDistrictChange={onDistrictChange}
          onBlockChange={onBlockChange}
          onSchoolChange={onSchoolChange}
        />
        <ReportDataTable
          title={`${activeMeta.label} - Full Comparison (${levelNoun})`}
          hint={canDrill ? drillHint(level) : undefined}
          icon="pi pi-table"
          data={tableRows as unknown as Record<string, unknown>[]}
          loading={loading}
          // columns={tableColumns}
          columns={
            level === 'school' && tableRows.length === 1
              ? [{ ...tableColumns[0], type: 'text' }, ...tableColumns.slice(1)]
              : tableColumns
          }
          exportFileName={`${activeType}-comparison`}
          searchPlaceholder={drillSearchPlaceholder(level)}
          rows={15}
          rowsPerPageOptions={[15, 30, 45]}
          equalColumns
          onRowClick={handleDistrictTableRowClick}
        />
      </div>
    </div>
  );
};

export default InfrastructureComparison;
